/**
 * quote-widget.js — hero quote card.
 * Category tabs + delegated TKSearch component for autocomplete.
 * See .t/arch/tk-search/constraints.yaml.
 */
(function () {
  "use strict";

  function $$(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  function devicesAll() {
    const tk = window.TKCatalog || {};
    return Array.isArray(tk.models) ? tk.models : (Array.isArray(window.DEVICES) ? window.DEVICES : []);
  }
  function deviceTypes() {
    return Array.isArray(window.DEVICE_TYPES) && window.DEVICE_TYPES.length
      ? window.DEVICE_TYPES
      : [
          { id: "phone",   label: "Smartphones" },
          { id: "tablet",  label: "Tablets" },
          { id: "laptop",  label: "Mac" },
          { id: "console", label: "Game Systems" },
        ];
  }
  function categoryForType(t) {
    const map = window.TK_TYPE_CATEGORY || { phone: "smartphones", tablet: "tablets", laptop: "mac", console: "game-systems" };
    return map[t] || null;
  }
  function devicesByType(typeId) {
    const cat = categoryForType(typeId);
    if (!cat) return [];
    return devicesAll().filter((d) => d.category_slug === cat);
  }
  function parsePriceCents(s) {
    if (s == null) return 0;
    const t = String(s).replace(/[^0-9.]/g, '');
    if (!t) return 0;
    const n = parseFloat(t);
    if (!isFinite(n) || n <= 0) return 0;
    // Dollars in, cents out — see the note in product-sell-v2.js; the old
    // "no decimal and >= 1000 must be cents" guess read $1,200 as $12.00.
    return Math.round(n * 100);
  }
  function fmtDollarsFromUpto(upto) {
    if (upto == null) return '';
    const s = String(upto).trim();
    if (!s) return '';
    let n = parseFloat(s.replace(/[^0-9.]/g, ''));
    if (!isFinite(n) || n <= 0) return '';
    return '$' + Math.round(n).toLocaleString();
  }
  function topModels(typeId, limit) {
    const all = devicesByType(typeId).slice();
    all.sort((a, b) => parsePriceCents(b.upto) - parsePriceCents(a.upto));
    return all.slice(0, limit || 4);
  }
  function escHtml(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c])); }

  function typeIconSvg(id) {
    const s = 'currentColor';
    if (id === "phone")   return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="'+s+'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="7" y="2" width="10" height="20" rx="2"/><line x1="11" y1="18" x2="13" y2="18"/></svg>';
    if (id === "tablet")  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="'+s+'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="3" width="16" height="18" rx="2"/><line x1="11" y1="18" x2="13" y2="18"/></svg>';
    if (id === "laptop")  return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="'+s+'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="5" width="18" height="11" rx="1.5"/><path d="M2 19h20"/></svg>';
    if (id === "console") return '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="'+s+'" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 12h4M8 10v4"/><circle cx="15" cy="11" r="1"/><circle cx="17" cy="13" r="1"/><rect x="2" y="7" width="20" height="10" rx="4"/></svg>';
    return '';
  }

  function init(root) {
    if (!root || root.dataset.qwInit === "1") return;
    root.dataset.qwInit = "1";

    const types = deviceTypes();
    const firstWithModels = types.find((t) => devicesByType(t.id).length > 0) || types[0];

    const state = { type: firstWithModels.id };

    function render() {
      const search = (window.TKSearch && typeof TKSearch.render === 'function')
        ? TKSearch.render({
            variant: 'widget',
            on_pick: 'navigate',
            category_filter: categoryForType(state.type) || '',
            placeholder: 'Search for your ' + (types.find(t => t.id === state.type)?.label.toLowerCase().replace(/s$/, '') || 'device') + '...',
            max_results: 8,
          })
        : '<p>Search unavailable.</p>';

      root.innerHTML = `
        <div class="qw">
          <header class="qw__head">
            <span class="qw__kicker"><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13 2 4 14h6l-1 8 9-12h-6z"/></svg>Instant · Free · No obligation</span>
            <h2>Get your quote</h2>
            <p>See what your device is worth in seconds.</p>
          </header>

          <div class="qw__types" role="group" aria-label="Device type">
            ${types.map((t) => {
              const has = devicesByType(t.id).length > 0;
              const active = state.type === t.id;
              return `<button type="button" aria-pressed="${active}" data-type="${t.id}" class="qw__type ${active ? "is-active" : ""} ${has ? "" : "is-disabled"}" ${has ? "" : "disabled"} title="${t.label}">
                <span class="qw__type-icon" aria-hidden="true">${typeIconSvg(t.id)}</span>
                <span class="qw__type-label">${t.label}</span>
              </button>`;
            }).join("")}
          </div>

          ${search}

          ${(function () {
            const top = topModels(state.type, 3);
            if (!top.length) return '';
            return '<ul class="qw__top">' + top.map(function (m) {
              const price = fmtDollarsFromUpto(m.upto);
              const href = '/products/' + (m.slug || '');
              const img = m.image ? '<img src="' + escHtml(m.image) + '" alt="" loading="lazy" decoding="async" width="56" height="56">' : '';
              return '<li><a href="' + escHtml(href) + '" class="qw__top-row">' +
                '<span class="qw__top-thumb" aria-hidden="true">' + img + '</span>' +
                '<span class="qw__top-meta">' +
                  '<strong>' + escHtml(m.name || m.slug) + '</strong>' +
                  (price ? '<span class="qw__top-price">up to <b>' + escHtml(price) + '</b></span>' : '') +
                '</span>' +
                '<svg class="qw__top-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8h10m-4-4 4 4-4 4" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
                '</a></li>';
            }).join('') + '</ul>';
          })()}

          <p class="qw__note">Your quote stays locked for 14 days.</p>
        </div>`;
      bind();
      if (window.TKSearch && typeof TKSearch.bindAll === 'function') TKSearch.bindAll();
    }

    function bind() {
      $$("[data-type]", root).forEach((b) => b.addEventListener("click", () => {
        if (b.hasAttribute("disabled")) return;
        state.type = b.getAttribute("data-type");
        render();
      }));
    }

    render();
    if (!root._qwCatalogBound) {
      root._qwCatalogBound = true;
      window.addEventListener("tk-catalog-updated", render);
    }
  }

  function ready() {
    document.querySelectorAll("[data-quote-widget]").forEach(init);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", ready);
  } else {
    ready();
  }

  window.tkQuoteWidget = { init };
})();
